﻿import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useUser } from '../../../../context/UserContext';
import { useQuery } from '@tanstack/react-query';
import { GetAgentRequests } from '../../../../api/sfd';
import LoadingIndicator from '../../../../components/Global/loading-indicator/loading-indicator';
import AgentRequestDownloadWidget from './AgentRequestDownloadWidget';

export default function AgentRequestsTable() {
  const user = useUser();
  const [columns, setColumns] = useState([]);

  const agentRequestsInfo = useQuery({
    queryKey: ['AgentRequests'],
    queryFn: () => GetAgentRequests(user.npnID)(),
    enabled: user != null && !user.isHO,
  });

  useEffect(() => {
    if (agentRequestsInfo.isSuccess && agentRequestsInfo.data) {
      if (agentRequestsInfo.data.agentRequestsList != null) {
        let npnToCompare = user.npnID.trim();
        let showDownlines = false;

        for (let i = 0; i < agentRequestsInfo.data.agentRequestsList.length; i++) {
          if (agentRequestsInfo.data.agentRequestsList[i].npnID.trim() !== npnToCompare) {
            showDownlines = true;
            break;
          }
        }

        if (showDownlines) {
          setColumns([
            { field: 'documentName', headerName: 'Document Name', flex: 2, align: 'left' },
            {
              field: 'requestDate',
              headerName: 'Expires In',
              flex: 2,
              renderCell: ({ row }) => <DateDifference reqDate={row.requestDate} />,
            },
            { field: 'printName', headerName: 'Agent Name', flex: 2, align: 'left' },
            {
              field: 'Action',
              headerName: 'Action',
              flex: 1,
              description: 'This column has a value getter and is not sortable.',
              sortable: false,
              renderCell: ({ row }) => (
                <AgentRequestDownloadWidget
                  isExpired={calculateDifference(row.requestDate)}
                  docID={row.documentID}
                  reqID={row.requestID}
                />
              ),
            },
          ]);
        } else {
          setColumns([
            { field: 'documentName', headerName: 'Document Name', flex: 2, align: 'left' },
            {
              field: 'requestDate',
              headerName: 'Expires In',
              flex: 2,
              renderCell: ({ row }) => <DateDifference reqDate={row.requestDate} />,
            },
            {
              field: 'Action',
              headerName: 'Action',
              flex: 1,
              description: 'This column has a value getter and is not sortable.',
              sortable: false,
              renderCell: ({ row }) => (
                <AgentRequestDownloadWidget
                  isExpired={calculateDifference(row.requestDate)}
                  docID={row.documentID}
                  reqID={row.requestID}
                />
              ),
            },
          ]);
        }
      }
    }
  }, [agentRequestsInfo.isSuccess, agentRequestsInfo.data]);

  const calculateDifference = (reqDate) => {
    const maxDaysToExpire = 5;
    const start = new Date(reqDate);

    const expirationDate = new Date(start);
    expirationDate.setDate(start.getDate() + maxDaysToExpire);

    const currentDate = new Date();

    const timeDifference = expirationDate - currentDate;

    if (timeDifference > 0) {
      const daysRemaining = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

      if (daysRemaining >= 1) {
        return `${daysRemaining} ${daysRemaining === 1 ? 'Day' : 'Days'}`;
      } else {
        const hoursRemaining = Math.floor(timeDifference / (1000 * 60 * 60));

        if (hoursRemaining >= 1) {
          return `${hoursRemaining} ${hoursRemaining === 1 ? 'Hour' : 'Hours'}`;
        } else {
          const minutesRemaining = Math.floor(timeDifference / (1000 * 60));
          return `${minutesRemaining} ${minutesRemaining === 1 ? 'Minute' : 'Minutes'}`;
        }
      }
    }

    return 'Expired';
  };

  function DateDifference({ reqDate }) {
    const difference = calculateDifference(reqDate);
    return difference;
  }

  const rowData = agentRequestsInfo.data?.agentRequestsList?.map((cl, index) => ({
    id: `row-${index}`,
    requestID: cl.requestID,
    documentID: cl.documentID,
    documentName: cl.documentName,
    requestDate: cl.requestDate,
    printName: cl.printName,
    rowIndex: index,
  }));

  if (agentRequestsInfo.isError)
    return (
      <div style={{ width: '100%', fontSize: '2em', textAlign: 'center', margin: '2em 0' }}>
        No Data to Display
      </div>
    );

  return (
    <div className="agent-requests-table">
      <div style={{ height: 500, width: '100%' }}>
        {agentRequestsInfo.isPending || agentRequestsInfo.isFetching ? (
          <LoadingIndicator />
        ) : (
          <DataGrid
            sx={{
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'transparent',
              },
              '& .MuiDataGrid-row.Mui-hovered': {
                backgroundColor: 'transparent',
              },
              '& p': {
                margin: 0,
              },
              '& .MuiTablePagination-actions': {
                marginTop: 0,
              },
            }}
            disableRowSelectionOnClick={true}
            disableCellSelection
            rows={rowData}
            columns={columns}
            disableColumnMenu
          />
        )}
      </div>
    </div>
  );
}
